import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  profile: { raisonSociale: '', logo: null, addresses: [], phones: [], emails: [], socialLinks: [] },
  loading: false,
  errors: []
};

export const fetchOrganization = createAsyncThunk(
  'organization/fetchOrganization',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // The API response carries an ETag but no Cache-Control, so browsers may serve a
      // stale cached copy for this repeatedly-called GET without hitting the network at
      // all. A cache-busting query param + explicit no-cache headers force a fresh fetch.
      const response = await api.get('/organization', {
        params: { _: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrganization = createAsyncThunk(
  'organization/updateOrganization',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put('/organization', body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadOrganizationLogo = createAsyncThunk(
  'organization/uploadOrganizationLogo',
  async (file, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/organization/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeOrganizationLogo = createAsyncThunk(
  'organization/removeOrganizationLogo',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete('/organization/logo');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addOrganizationAddress = createAsyncThunk(
  'organization/addOrganizationAddress',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/organization/addresses', body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrganizationAddress = createAsyncThunk(
  'organization/updateOrganizationAddress',
  async ({ addressId, ...body }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/organization/addresses/${addressId}`, body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteOrganizationAddress = createAsyncThunk(
  'organization/deleteOrganizationAddress',
  async (addressId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/organization/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addOrganizationPhone = createAsyncThunk(
  'organization/addOrganizationPhone',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/organization/phones', body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrganizationPhone = createAsyncThunk(
  'organization/updateOrganizationPhone',
  async ({ phoneId, ...body }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/organization/phones/${phoneId}`, body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteOrganizationPhone = createAsyncThunk(
  'organization/deleteOrganizationPhone',
  async (phoneId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/organization/phones/${phoneId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addOrganizationEmail = createAsyncThunk(
  'organization/addOrganizationEmail',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/organization/emails', body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrganizationEmail = createAsyncThunk(
  'organization/updateOrganizationEmail',
  async ({ emailId, ...body }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/organization/emails/${emailId}`, body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteOrganizationEmail = createAsyncThunk(
  'organization/deleteOrganizationEmail',
  async (emailId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/organization/emails/${emailId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addOrganizationSocialLink = createAsyncThunk(
  'organization/addOrganizationSocialLink',
  async (body, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/organization/social-links', body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateOrganizationSocialLink = createAsyncThunk(
  'organization/updateOrganizationSocialLink',
  async ({ linkId, ...body }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/organization/social-links/${linkId}`, body);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteOrganizationSocialLink = createAsyncThunk(
  'organization/deleteOrganizationSocialLink',
  async (linkId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/organization/social-links/${linkId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const uploadOrganizationSocialLinkIcon = createAsyncThunk(
  'organization/uploadOrganizationSocialLinkIcon',
  async ({ linkId, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/organization/social-links/${linkId}/icon`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const removeOrganizationSocialLinkIcon = createAsyncThunk(
  'organization/removeOrganizationSocialLinkIcon',
  async (linkId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/organization/social-links/${linkId}/icon`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrganization.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchOrganization.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchOrganization.rejected, (state, action) => {
        state.loading = false;
        state.errors = action.payload;
      })
      .addMatcher(
        (action) => action.type.startsWith('organization/') && action.type.endsWith('/fulfilled') && action.type !== fetchOrganization.fulfilled.type,
        (state, action) => {
          state.profile = action.payload;
        }
      );
  }
});

export default organizationSlice.reducer;
